/**
 * Carries the page a user was heading for across a forced trip to the login screen.
 *
 * Mainly for GG push notifications: tapping one when the session has lapsed used to
 * drop the user on the dashboard with the conversation link silently discarded.
 * sessionStorage rather than router state because login finishes with a full reload
 * (see hardRefresh in LoginPage), which throws in-memory router state away.
 */
const KEY = "elg5-post-login-redirect";

let suppressNextCapture = false;

function isSafeInternalPath(target: string | null): target is string {
  return (
    !!target &&
    target.startsWith("/") &&
    // "//host" is protocol-relative — it would leave the origin.
    !target.startsWith("//") &&
    !target.startsWith("/auth")
  );
}

/**
 * Deliberate logout: don't treat the page the user was on as somewhere to return to.
 * Without this, signing out and back in — possibly as a different person — would drop
 * you on the previous session's page instead of your own landing page.
 *
 * Set before clearing auth state, because the redirect that would capture the target
 * renders in the same tick, after the caller returns.
 */
export function suppressPostLoginCapture(): void {
  suppressNextCapture = true;
}

export function rememberPostLoginTarget(target: string): void {
  if (suppressNextCapture) {
    suppressNextCapture = false;
    return;
  }
  if (!isSafeInternalPath(target)) return;
  // "/" is not a destination, it's the absence of one — and capturing it would send
  // contractors to the dashboard instead of their own landing page after login.
  if (target === "/" || target.startsWith("/?")) return;
  try {
    sessionStorage.setItem(KEY, target);
  } catch {
    // Storage unavailable (private mode) — the user just lands on the default page.
  }
}

/** Read and consume the pending target. Returns null when there is nothing to resume. */
export function takePostLoginTarget(): string | null {
  try {
    const target = sessionStorage.getItem(KEY);
    sessionStorage.removeItem(KEY);
    return isSafeInternalPath(target) ? target : null;
  } catch {
    return null;
  }
}
